import React, {useEffect, useState} from 'react';
import {CalendarApiService} from './CalendarMobileViewOnline';

interface CalendarDesktopViewProps {
  schedule: CalendarApiService[];
  value: Date | null;
  onChange: (date: Date | null) => void;
  productId: number | undefined;
  doctorId?: string;
}

const CalendarDesktopViewOnline = ({
  value,
  onChange,
  productId,
  doctorId,
  schedule
}: CalendarDesktopViewProps) => {
  const [currentWeekStart, setCurrentWeekStart] = useState<Date>(() => {
    // Встановлюємо початковий тиждень на поточний тиждень
    const today = new Date();
    const day = today.getDay();
    const diff = today.getDate() - day + (day === 0 ? -6 : 1); // Понеділок як перший день
    const weekStart = new Date(today);
    weekStart.setDate(diff);
    return weekStart;
  });
  const [selectedDate, setSelectedDate] = useState<Date | null>(value);

  // Польські назви днів тижня
  const dayNames = [
    'Poniedziałek',
    'Wtorek',
    'Środa',
    'Czwartek',
    'Piątek',
    'Sobota',
    'Niedziela'
  ];

  // Польські назви місяців
  const monthNames = [
    'Styczeń',
    'Luty',
    'Marzec',
    'Kwiecień',
    'Maj',
    'Czerwiec',
    'Lipiec',
    'Sierpień',
    'Wrzesień',
    'Październik',
    'Listopad',
    'Grudzień'
  ];

  // Функція для отримання початку тижня (понеділок)
  const getWeekStart = (date: Date): Date => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Понеділок як перший день
    return new Date(d.setDate(diff));
  };

  // Функція для отримання кінця тижня (неділя)
  const getWeekEnd = (date: Date): Date => {
    const weekStart = getWeekStart(date);
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);
    return weekEnd;
  };

  // Функція для форматування діапазону тижня
  const formatWeekRange = (start: Date, end: Date): string => {
    const startDay = start.getDate();
    const endDay = end.getDate();
    const month = monthNames[start.getMonth()];
    const year = start.getFullYear();

    if (start.getMonth() === end.getMonth()) {
      return `${startDay} ${month} - ${endDay} ${month} ${year}`;
    } else {
      const endMonth = monthNames[end.getMonth()];
      return `${startDay} ${month} - ${endDay} ${endMonth} ${year}`;
    }
  };

  // Функція для фільтрації доступних дат
  const getFilteredDates = () => {
    if (!Array.isArray(schedule)) {
      return [];
    }

    const now = new Date();

    return (schedule as CalendarApiService[])
      .filter((item) => {
        const itemDate = new Date(item.Date);
        // Normalize 'now' to the start of today for date-only comparisons
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

        // A date is valid if it's today or in the future
        if (itemDate < today) {
          return false;
        }

        const validRows = item.Rows.filter((row) => {
          const doctorMatch =
            !doctorId ||
            doctorId === '' ||
            String(row.DoctorId) === String(doctorId);

          // The time of the slot must be in the future
          const time = new Date(row.Time);
          const timeValid = time > now;

          return doctorMatch && timeValid;
        });

        return validRows.length > 0;
      })
      .map((item) => new Date(item.Date));
  };

  // Перевірка доступності дня
  const isDayAvailable = (date: Date) => {
    const filteredDates = getFilteredDates();
    console.log(filteredDates, "filteredDates")
    return filteredDates.some(
      (d) =>
        d.getFullYear() === date.getFullYear() &&
        d.getMonth() === date.getMonth() &&
        d.getDate() === date.getDate()
    );
  };

  // Генерація днів тижня
  const getWeekDays = (): Date[] => {
    const days: Date[] = [];
    const start = new Date(currentWeekStart);

    for (let i = 0; i < 7; i++) {
      const day = new Date(start);
      day.setDate(start.getDate() + i);
      days.push(day);
    }

    return days;
  };

  const findFirstAvailableDateInWeek = (weekStart: Date): Date | null => {
    const days: Date[] = [];
    const start = new Date(weekStart);
    for (let i = 0; i < 7; i++) {
      const day = new Date(start);
      day.setDate(start.getDate() + i);
      days.push(day);
    }

    for (const day of days) {
      if (isDayAvailable(day)) {
        return day;
      }
    }

    return null;
  };

  // Перехід до наступного тижня
  const goToNextWeek = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const nextWeek = new Date(currentWeekStart);
    nextWeek.setDate(currentWeekStart.getDate() + 7);
    setCurrentWeekStart(nextWeek);

    const firstAvailable = findFirstAvailableDateInWeek(nextWeek);
    setSelectedDate(firstAvailable);
    onChange(firstAvailable);
  };

  // Перехід до попереднього тижня
  const goToPreviousWeek = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Переходимо на попередній тиждень
    const prevWeek = new Date(currentWeekStart);
    prevWeek.setDate(currentWeekStart.getDate() - 7);

    const today = new Date();
    const currentWeekStartToday = getWeekStart(today);

    // Дозволяємо перехід тільки якщо попередній тиждень не раніше поточного
    if (prevWeek.getTime() >= currentWeekStartToday.getTime()) {
      setCurrentWeekStart(prevWeek);
      const firstAvailable = findFirstAvailableDateInWeek(prevWeek);
      setSelectedDate(firstAvailable);
      onChange(firstAvailable);
    } else {
      // Якщо намагаємося перейти на минулий тиждень, переходимо до поточного
      setCurrentWeekStart(currentWeekStartToday);
      const firstAvailable =
        findFirstAvailableDateInWeek(currentWeekStartToday);
      setSelectedDate(firstAvailable);
      onChange(firstAvailable);
    }
  };

  // Перевірка, чи можна перейти на попередній тиждень
  const canGoToPreviousWeek = () => {
    const today = new Date();
    const currentWeekStartToday = getWeekStart(today);
  
    const isSameWeek =
      currentWeekStart.getFullYear() === currentWeekStartToday.getFullYear() &&
      currentWeekStart.getMonth() === currentWeekStartToday.getMonth() &&
      currentWeekStart.getDate() === currentWeekStartToday.getDate();
  
    const isNotCurrentWeek = !isSameWeek;
    return isNotCurrentWeek;
  };

  // Обробка вибору дня
  const handleDaySelect = (date: Date) => {
    if (isDayAvailable(date)) {
      setSelectedDate(date);
      onChange(date);
    }
  };

  // Автоматичний вибір першої доступної дати при зміні лікаря
  useEffect(() => {
    if (Array.isArray(schedule)) {
      const availableDates = getFilteredDates();

      if (availableDates.length > 0) {
        const sortedDates = [...availableDates].sort(
          (a, b) => a.getTime() - b.getTime()
        );

        if (!selectedDate || !isDayAvailable(selectedDate)) {
          const firstAvailableDate = sortedDates[0];
          if (firstAvailableDate) {
            setSelectedDate(firstAvailableDate);
            onChange(firstAvailableDate);

            // Переходимо до тижня з першою доступною датою
            const firstDateWeekStart = getWeekStart(firstAvailableDate);

            // Перевіряємо, чи не намагаємося перейти на минулий тиждень
            const today = new Date();
            const currentWeekStartToday = getWeekStart(today);

            if (
              firstDateWeekStart.getTime() >= currentWeekStartToday.getTime() &&
              firstDateWeekStart.getTime() !== currentWeekStart.getTime()
            ) {
              setCurrentWeekStart(firstDateWeekStart);
            } else if (
              firstDateWeekStart.getTime() < currentWeekStartToday.getTime()
            ) {
              // Якщо перша доступна дата в минулому, переходимо до поточного тижня
              setCurrentWeekStart(currentWeekStartToday);
            }
          }
        }
      }
    }
  }, [doctorId, schedule]);

  // Оновлення вибраної дати при зміні value
  useEffect(() => {
    setSelectedDate(value);
    if (value) {
      // Якщо вибрана дата не в поточному тижні, переходимо до тижня з цією датою
      const valueWeekStart = getWeekStart(value);

      // Перевіряємо, чи не намагаємося перейти на минулий тиждень
      const today = new Date();
      const currentWeekStartToday = getWeekStart(today);

      if (
        valueWeekStart.getTime() >= currentWeekStartToday.getTime() &&
        valueWeekStart.getTime() !== currentWeekStart.getTime()
      ) {
        setCurrentWeekStart(valueWeekStart);
      } else if (valueWeekStart.getTime() < currentWeekStartToday.getTime()) {
        // Якщо вибрана дата в минулому, переходимо до поточного тижня
        setCurrentWeekStart(currentWeekStartToday);
      }
    }
  }, [value]);

  const weekDays = getWeekDays();
  const weekEnd = getWeekEnd(currentWeekStart);

  return (
    <div className='w-full mx-auto'>
      <h3 className='mb-1 text-lg font-semibold text-center sm:text-left'>
        Wybierz dzień i czas
      </h3>

      {/* Навігація по тижнях */}
      <div className='flex items-center justify-between mb-6'>
        {canGoToPreviousWeek() && (
          <div className='flex items-center space-x-2'>
            <button
              type='button'
              onClick={goToPreviousWeek}
              className='text-primary text-base font-semibold'
            >
              &lt;&lt; Poprzedni tydzień
            </button>
          </div>
        )}

        <div className='text-center'>
          <span className='font-semibold text-base'>Bieżący tydzień</span>
          <span className='text-base ml-4'>
            {formatWeekRange(currentWeekStart, weekEnd)}
          </span>
        </div>

        <button
          type='button'
          onClick={goToNextWeek}
          className='text-primary text-base font-semibold'
        >
          Następny tydzień &gt;&gt;
        </button>
      </div>

      {/* Дні тижня */}
      <div className='grid grid-cols-7 gap-2'>
        {weekDays.map((day, index) => {
         
          const isAvailable = isDayAvailable(day);
           console.log(isAvailable, "isAvailable")
          const isSelected =
            selectedDate &&
            selectedDate.getFullYear() === day.getFullYear() &&
            selectedDate.getMonth() === day.getMonth() &&
            selectedDate.getDate() === day.getDate();

          return (
            <div
              key={day.getTime()}
              onClick={() => handleDaySelect(day)}
              className={`
                p-3 rounded-lg border border-[var(--border-accent)] cursor-pointer transition-all duration-200
                ${
                  isSelected
                    ? 'bg-primary text-white border-accent'
                    : isAvailable
                    ? 'bg-[#FFFEFA] text-[var(--text-primary)]  hover:border-accent-300 hover:shadow-md'
                    : 'bg-[#E7E7E7] text-[#B0B0B0] border-[#E7E7E7]  cursor-not-allowed'
                }
              `}
            >
              <div className='text-center'>
                <div
                  className={`text-base font-bold ${
                    isSelected
                      ? 'text-[var(--text-primary)]'
                      : isAvailable
                      ? 'text-[var(--text-primary)]'
                      : 'text-[#B0B0B0]'
                  }`}
                >
                  {day.getDate()}
                </div>
                <div
                  className={`text-base font-medium ${
                    isSelected
                    ? 'text-[var(--text-primary)]'
                    : isAvailable
                    ? 'text-[var(--text-primary)]'
                    : 'text-[#B0B0B0]'
                  }`}
                >
                  {dayNames[index]}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CalendarDesktopViewOnline;
